/**
 * Turn one OrderDraft into the `cartInfo` object that `saveNewSale` consumes.
 *
 * This is the load-bearing module of the import: an imported sale must be
 * indistinguishable from one rung up at the till, or reports, stock and the books
 * disagree. It therefore uses the SAME pricing functions NewSaleScreen uses
 * (utils/cartPricing, re-exported by components/CartView) rather than its own math,
 * and mirrors NewSaleScreen's cartInfo field by field.
 *
 * Deliberate differences from a till sale, all of them because the file cannot express
 * them (see the design spec):
 *  - no discount (0 % / 0 ₸) and no purchase method
 *  - no loyalty accrual or redemption: cashback/punch card are off and bonusAction is 0,
 *    so an imported historical sale does not retroactively mint bonus points. The
 *    client's totalPurchasedValue / lastPurchaseDate still update inside saveNewSale.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- product/client/payment docs are untyped across Firestore and Supabase read modes */

import {
  discountPrice,
  finalItemPrice,
  finalTotalCartPrice,
  itemPriceWithoutModifiers,
  totalCartPrice,
  totalCartPriceWithPriceModificators,
  totalCartPurchasePrice,
  totalCartQuantity,
} from '../cartPricing'
import { getDailyStatID, storeLocalDateAtTime } from '../timezoneUtils'
import { type OrderDraft } from './types'

export interface BuildSaleInfoContext {
  store: string
  organizationId: string
  cashierUID: string
  /** Payment method object from paymentMethodsDict, or null for a postPaid (debt) sale. */
  paymentMethod: any | null
  /** Resolved client document, or null when the order has no client. */
  client: any | null
  /** One id per import run, stamped on every sale it creates. */
  importBatchId: string
  /** Price types from priceTypesAtom, passed through to the pricing helpers. */
  priceTypes?: any[] | null
}

/**
 * A built cart line. Structurally compatible with the `CartItem` the pricing helpers
 * expect (quantity plus the price fields), carrying the rest of the product document.
 */
export interface ImportCartItem extends Record<string, any> {
  quantity: number
}

export interface BuiltSale {
  cartInfo: Record<string, any>
  saleDate: Date
}

/**
 * Build one cart item. Shape mirrors what NewSaleScreen puts in `cartItems`: the whole
 * product document plus quantity and the computed prices.
 */
const buildCartItem = (
  product: any,
  quantity: number,
  store: string,
  priceTypes: any[] | null
): ImportCartItem => {
  const item = { ...product, quantity }
  return {
    ...item,
    finalItemPrice: finalItemPrice(item, store, 0, null, null, priceTypes),
    itemPriceWithoutModifiers: itemPriceWithoutModifiers(item, store, 0, null, priceTypes),
    priceType: null,
  }
}

/** The cart lines an order will be written with. */
export const buildCartItemsForOrder = (
  order: OrderDraft,
  store: string,
  priceTypes: any[] | null = null
): ImportCartItem[] =>
  order.items.map((item) => buildCartItem(item.product, item.quantity, store, priceTypes))

/**
 * What one order will charge, for the preview total.
 *
 * The import applies no discount and no price modificators, so this is the same number
 * `buildSaleInfo` puts in `finalTotalCartPrice` — computed with the same helper, so the
 * figure the user confirms is the figure that reaches the books.
 */
export const orderTotal = (
  order: OrderDraft,
  store: string,
  priceTypes: any[] | null = null
): number => totalCartPrice(buildCartItemsForOrder(order, store, priceTypes), store, null, priceTypes)

export const buildSaleInfo = (order: OrderDraft, context: BuildSaleInfoContext): BuiltSale => {
  const { store, organizationId, cashierUID, paymentMethod, client, importBatchId } = context
  const priceTypes = context.priceTypes ?? null

  const saleDate = storeLocalDateAtTime(store, order.date)
  const cartItems = buildCartItemsForOrder(order, store, priceTypes)

  const cartInfo: Record<string, any> = {
    totalCartPrice: totalCartPrice(cartItems, store, null, priceTypes),
    totalCartPriceWithPriceModificators: totalCartPriceWithPriceModificators(
      cartItems,
      store,
      null,
      null,
      priceTypes
    ),
    finalTotalCartPrice: finalTotalCartPrice(
      0,
      0,
      cartItems,
      store,
      client,
      0,
      false,
      false,
      0,
      null,
      null,
      priceTypes
    ),
    priceType: null,
    priceTypes: priceTypes,
    totalPurchasePrice: totalCartPurchasePrice(cartItems),
    totalCartQuantity: totalCartQuantity(cartItems),
    discountPrice: discountPrice(0, 0, cartItems, store, null, priceTypes),
    cashierUID,
    cartItems,
    comment: '',
    tableNumber: '',
    paymentMethod: paymentMethod || null,
    purchaseMethod: null,
    priceModificators: null,
    store,
    organizationId,
    saleDate,
    createdDate: new Date(),
    dailyStatID: getDailyStatID(store, saleDate),
    discountPercentage: 0,
    discountAmount: 0,
    // Import provenance — traceable and revertable. Plumbed through the backend DTO and
    // the sales table too, so Firestore and Supabase agree (the API strips unknown fields).
    saleSource: 'import',
    importBatchId,
  }

  if (!paymentMethod) {
    // No payment method means the money has not arrived: record it as a debt, exactly
    // like the postPaid path in NewSaleScreen. saveNewSale then writes no transaction.
    cartInfo.postPaid = true
  }

  if (client != null) {
    cartInfo.client = { ...client }
    cartInfo.clientId = client.id
    cartInfo.clientPhone = client.phoneNumber ?? client.phone ?? null
    cartInfo.bonusAction = 0
    cartInfo.cashbackEnabled = false
    cartInfo.bonusReduction = 0
    cartInfo.bonusAccumulation = 0
    cartInfo.punchCardEnabled = false
    cartInfo.punchCardReduction = 0
  }

  return { cartInfo, saleDate }
}
